import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DatePicker from "../date-picker/DatePicker";
import { Button } from "../ui/button";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const complianceSchema = z.object({
  date: z.string().min(1, "Date is required"),
  time: z.string().min(1, "Time is required"),
  log: z
    .string()
    .min(1, "Notes are required")
    .max(500, "Notes must be less than 500 characters"),
});

const ComplianceDialog = ({ onSubmit, type = "doctor" }) => {
  const currentDate = new Date();
  const currentTime = currentDate.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });

  const form = useForm({
    resolver: zodResolver(complianceSchema),
    defaultValues: {
      date: currentDate.toISOString(),
      time: currentTime,
      log: "",
    },
  });

  const handleSubmit = (data) => {
    // Ensure date is properly formatted as ISO string
    const formattedData = {
      ...data,
      date: data.date instanceof Date ? data.date.toISOString() : data.date,
    };

    onSubmit(formattedData);

    // Reset form with fresh date/time
    const newCurrentDate = new Date();
    const newCurrentTime = newCurrentDate.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });

    form.reset({
      date: newCurrentDate.toISOString(),
      time: newCurrentTime,
      log: "",
    });
  };

  const isDoctor = type === "doctor";
  const title = isDoctor ? "Add Doctor's Note" : "Add Patient Log";
  const placeholder = isDoctor
    ? "Enter detailed description..."
    : "How are you feeling? Any side effects or observations...";
  const label = isDoctor ? "Description" : "Notes";
  const buttonText = isDoctor ? "Add Note" : "Add Log";

  return (
    <DialogContent className="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription className="sr-only">
          Add a new {isDoctor ? "note" : "log"} entry. Date and time are
          automatically set to current values.
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <div className="pointer-events-none opacity-60">
                      <DatePicker
                        field={field}
                        label=""
                        className="bg-muted cursor-not-allowed h-[40px]"
                      />
                    </div>
                  </FormControl>
                  <FormDescription className="sr-only">
                    Current date (auto-filled)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Time</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="time"
                      disabled={true}
                      className="bg-muted h-[40px]"
                    />
                  </FormControl>
                  <FormDescription className="sr-only">
                    Current time (auto-filled)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="log"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={placeholder}
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {isDoctor
                    ? "Detailed description of the note"
                    : "Add any notes about how you're feeling"}{" "}
                  (max 500 characters)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant={"primary"}>
              {buttonText}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};

export default ComplianceDialog;
